import { useState } from 'react';
import { Bitcoin, Check, Copy, Globe, Sparkles, Zap } from 'lucide-react';

import { GithubIcon } from '@/components/icons/GithubIcon';

import { useSiteOwner } from '@/hooks/useSiteOwner';
import { useToast } from '@/hooks/useToast';
import { njumpUrl, truncateMiddle } from '@/lib/owner';
import { siteConfig } from '@/config/site';

export function SiteFooter() {
  const owner = useSiteOwner();
  const { toast } = useToast();
  const [copiedKey, setCopiedKey] = useState<'npub' | 'ln' | null>(null);

  const copy = async (value: string, key: 'npub' | 'ln') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast({ title: 'Could not copy to clipboard', variant: 'destructive' });
    }
  };

  return (
    <footer className="border-t">
      <div className="container grid gap-10 py-14 md:grid-cols-[1.2fr_1fr]">
        <div className="space-y-4">
          <div className="flex items-center gap-2.5">
            <span className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-amber-500 text-primary-foreground">
              <Bitcoin className="size-5" />
            </span>
            <span className="font-mono text-base font-bold">
              {siteConfig.handle}
              <span className="text-primary">_</span>
            </span>
          </div>

          <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
            Open-source Bitcoin & Nostr developer. Everything here is signed, self-hosted,
            and surveillance-free.
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => copy(owner.npub, 'npub')}
              className="group flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 font-mono text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
              title="Copy npub"
            >
              <span className="size-1.5 rounded-full bg-primary" />
              {truncateMiddle(owner.npub, 12, 6)}
              {copiedKey === 'npub' ? (
                <Check className="size-3.5 text-green-500" />
              ) : (
                <Copy className="size-3.5" />
              )}
            </button>

            {owner.lightning && (
              <button
                onClick={() => copy(owner.lightning as string, 'ln')}
                className="group flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 font-mono text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                title="Copy lightning address"
              >
                <Zap className="size-3.5 text-primary" />
                {owner.lightning}
                {copiedKey === 'ln' ? (
                  <Check className="size-3.5 text-green-500" />
                ) : (
                  <Copy className="size-3.5" />
                )}
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 text-sm md:items-end">
          <a
            href={njumpUrl(owner.pubkey)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary"
          >
            <Zap className="size-4" />
            Nostr profile
          </a>
          {siteConfig.links.github && (
            <a
              href={siteConfig.links.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary"
            >
              <GithubIcon className="size-4" />
              GitHub
            </a>
          )}
          {siteConfig.links.website && (
            <a
              href={siteConfig.links.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary"
            >
              <Globe className="size-4" />
              Website
            </a>
          )}
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 flex items-center gap-1.5 font-mono text-xs text-muted-foreground transition-colors hover:text-primary"
          >
            <Sparkles className="size-3.5" />
            Vibed with Shakespeare
          </a>
        </div>
      </div>

      <div className="border-t py-5 text-center font-mono text-xs text-muted-foreground">
        © {new Date().getFullYear()} {owner.name} · no cookies, no trackers — just signed notes
      </div>
    </footer>
  );
}
